<template>
  <div class="p-8 space-y-4">
    <h2 class="text-2xl">Driver Wallets</h2>
    <div class>
      <div class="flex w-full">
        <div class="relative mt-4 mr-4">
          <label class="block">
            <span class="text-gray-700">Driver</span>
            <input
              v-model="selectedDriver"
              @keyup="searchDriver"
              class="form-input mt-1 w-full block"
              placeholder="Search drivers..."
            />
          </label>
          <transition
            enter-active-class="transition ease-out duration-100"
            enter-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <div v-if="driverSearchResults.length" class="absolute mt-1 inset-x-0 z-50">
              <div class="max-h-64 py-2 bg-white shadow rounded-md overflow-y-auto">
                <ul>
                  <li
                    v-for="(result, index) in driverSearchResults"
                    :key="result._id"
                    @click="selectDriver(index)"
                    class="flex items-center space-x-2 px-4 py-2 hover:bg-gray-200 cursor-pointer border-t"
                  >
                    <img
                      class="h-12 w-12 rounded-full object-cover"
                      :src="result.profileImage"
                      :alt="`${result.firstName} ${result.lastName}`"
                    />
                    <div>
                      <p>{{ `${result.firstName} ${result.lastName}` }}</p>
                      <p class="text-gray-600 text-sm font-semibold">{{ result.phoneNumber }}</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </transition>
        </div>

        <div class="relative mt-4 mr-4">
          <label class="block">
            <span class="text-gray-700">Account</span>
            <input
              v-model="selectedAccount"
              @keyup="searchAccount"
              class="form-input mt-1 w-full block"
              placeholder="Search account..."
            />
          </label>
          <transition
            enter-active-class="transition ease-out duration-100"
            enter-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <div v-if="accountSearchResults.length" class="absolute mt-1 inset-x-0 z-50">
              <div class="max-h-64 py-2 bg-white shadow rounded-md overflow-y-auto">
                <ul>
                  <li
                    v-for="(result, index) in accountSearchResults"
                    :key="result._id"
                    @click="selectAccount(index)"
                    class="flex items-center space-x-2 px-4 py-2 hover:bg-gray-200 cursor-pointer border-t"
                  >
                    <div>
                      <p>{{ `${result.firstName} ${result.lastName}` }}</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </transition>
        </div>

        <div v-if="accountId == null" class="relative mt-4">
          <div class="pr-3">
            <label class="block">
              <span class="text-gray-700">By</span>
              <select class="form-select w-full" v-model="by" @change="getAllWallets">
                <option :selected="by === 'all'" value="all">All</option>
                <option :selected="by === 'admin'" value="Admin">Admin</option>
                <option :selected="by === 'system'" value="System">System</option>
              </select>
            </label>
          </div>
        </div>

        <div class="relative mt-4">
          <div class="pr-3">
            <label class="block">
              <span class="text-gray-700">From</span>
              <v-date-picker :value="startDate" v-model="startDate" />
            </label>
          </div>
        </div>

        <div class="relative mt-4">
          <div class="pr-3">
            <label class="block">
              <span class="text-gray-700">To</span>
              <v-date-picker :value="endDate" v-model="endDate" />
            </label>
          </div>
        </div>
      </div>
    </div>
    <div>
      <div class="flex space-x-4">
        <h3 class="flex items-center space-x-4">
          <span>Wallets</span>
        </h3>
        <div class="flex" v-show="driverId">
          <div v-show="adding" class="w-64 relative">
            <input
              v-model="amount"
              type="number"
              min="1"
              class="form-input pl-10 w-full rounded-r-none"
              :class="amountError ? 'border-red-800 bg-red-200' : ''"
              ref="amountInput"
              placeholder="Enter the amount"
            />
            <span
              @click="adding = false"
              class="absolute inset-y-0 left-0 flex items-center px-2 text-gray-500 hover:text-gray-900 cursor-pointer"
            >
              <i class="material-icons">close</i>
            </span>
          </div>
          <div v-show="adding" class="w-84 relative">
            <input
              v-model="reason"
              type="text"
              class="form-input pl-10 w-full rounded-l-none rounded-r-none"
              placeholder="Enter reason"
            />
          </div>
          <button
            :disabled="busy"
            @click="handleClick"
            class="w-48 flex items-center justify-center space-x-2 rounded px-4 py-2 text-white border border-ilift-600"
            :class="{
              'rounded-l-none': adding,
              'bg-ilift-300 cursor-not-allowed': busy,
              'bg-ilift-500 hover:bg-ilift-600 focus:bg-ilift-600': !busy,
            }"
          >
            <i class="material-icons">account_balance_wallet</i>
            <span>Add Wallet</span>
          </button>
        </div>
      </div>

      <div class="inline-block min-w-full shadow rounded-lg overflow-hidden mt-4">
        <table class="min-w-full leading-normal">
          <thead>
            <tr>
              <th
                class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
              >Wallet ID</th>
              <th
                class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
              >Driver</th>
              <th
                class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
              >Amount</th>
              <th
                class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
              >By</th>
              <th
                class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
              >User</th>
              <th
                class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
              >
                Reason
              </th>
              <th
                class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
              >Last Transaction Date</th>
            </tr>
          </thead>
          <tbody v-if="wallets.length" class="relative">
            <tr v-for="wallet in wallets" :key="wallet._id">
              <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                <p class="whitespace-no-wrap">{{ wallet._id }}</p>
              </td>
              <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                <p class="whitespace-no-wrap">
                  {{
                  wallet.driver
                  ? wallet.driver.firstName + " " + wallet.driver.lastName
                  : "-"
                  }}
                </p>
                <p
                  class="whitespace-no-wrap text-gray-600"
                >{{ wallet.driver ? wallet.driver._id : "-" }}</p>
              </td>
              <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                <p class="text-gray-900 whitespace-no-wrap">{{ wallet.amount.toFixed(2) }}</p>
              </td>
              <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm">{{ wallet.by }}</td>
              <td
                class="px-5 py-5 border-b border-gray-200 bg-white text-sm"
              >{{ wallet.account ? wallet.account.firstName + " " + wallet.account.lastName : wallet.from ? wallet.from.firstName + " " + wallet.from.lastName : "-" }}</td>
              <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                {{ wallet.reason }}
              </td>
              <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm">{{ wallet.updatedAt }}</td>
            </tr>
            <div
              v-if="loading"
              class="absolute inset-0 h-full w-full flex items-center justify-center bg-black bg-opacity-25"
            >
              <i class="material-icons text-6xl spin">sync</i>
              <span class="block invisible">Loading...</span>
            </div>
          </tbody>
          <tbody v-else>
            <tr>
              <td colspan="6" class="bg-white py-4 text-center text-gray-600 font-semibold">
                <div v-if="loading" class="inline-block">
                  <i class="material-icons text-6xl spin">sync</i>
                  <span class="block invisible">Loading...</span>
                </div>
                <div v-else class="inline-block">
                  <i class="material-icons text-6xl">warning</i>
                  <span class="block">No Wallets!</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="flex justify-between items-end">
        <p class="text-gray-600">
          Showing
          <strong>{{ start }}</strong> to
          <strong>{{ end }}</strong> of
          <strong>{{ paginator.count }}</strong> entries
        </p>
        <div class="flex items-center space-x-4">
          <div>
            Per page:
            <select class="form-select" name="perPage" id="perPage" @change="setPerPage">
              <option :selected="perPage === '10'" value="10">10</option>
              <option :selected="perPage === '25'" value="25">25</option>
              <option :selected="perPage === '50'" value="50">50</option>
              <option :selected="perPage === '100'" value="100">100</option>
            </select>
          </div>
          <div class="space-x-2 flex">
            <button
              @click="goToPrev"
              type="button"
              class="flex items-center p-2 text-gray-500 hover:text-gray-800 outline-none focus:outline-none"
            >
              <i class="material-icons">chevron_left</i>
            </button>
            <button
              v-for="page in total"
              :key="`pageLink-${page}`"
              type="button"
              class="p-2"
              :class="{
                'text-gray-500 hover:text-gray-800': currentPage !== page,
                'text-gray-900': currentPage === page,
              }"
              :disabled="currentPage === page"
              @click="goToPage(page)"
            >{{ page }}</button>
            <button
              @click="goToNext"
              type="button"
              class="flex items-center p-2 text-gray-500 hover:text-gray-800 outline-none focus:outline-none"
            >
              <i class="material-icons">chevron_right</i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Pagination from "@/mixins/Pagination";

export default {
  mixins: [Pagination],
  data() {
    return {
      loading: false,
      wallets: [],
      selectedDriver: "",
      drivers: [],
      driverSearchResults: [],
      driverId: null,
      by: "all",
      accountId: null,
      startDate: null,
      endDate: null,
      accounts: [],
      adding: false,
      amount: 10,
      accountSearchResults: [],
      selectedAccount: "",
      reason: "Topup",
      amountError: false,
      busy: false
    };
  },
  watch: {
    amount() {
      this.amountError = false;
    },
    startDate() {
      this.getAllWallets();
    },
    endDate() {
      this.getAllWallets();
    },
    currentPage() {
      this.getAllWallets();
    },
    perPage() {
      this.getAllWallets();
    }
  },
  created() {
    var sd = new Date();
    sd.setDate(0);
    sd.setDate(1);
    var ed = new Date();
    ed.setMonth(ed.getMonth() + 1);
    ed.setDate(0);
    this.startDate = sd;
    this.endDate = ed;
    if (this.$route.query.driver) {
      this.driverId = this.$route.query.driver;
      this.adding = true;
    } else {
      this.getAllWallets();
    }
    console.log(this.$store.state.auth.account._id);
    this.$http
      .get("/drivers")
      .then(response => {
        this.drivers = response.data.data;
        let driver = this.drivers.find(driver => driver._id == this.driverId);
        if (driver) {
          this.selectedDriver = `${driver.firstName} ${driver.lastName} - ${driver.phoneNumber}`;
        }
      })
      .catch(err => console.log(err));

    this.$http
      .get("/accounts")
      .then(response => (this.accounts = response.data.data))
      .catch(err => console.log(err));
  },
  methods: {
    handleClick() {
      this.adding ? this.add() : this.openAdd();
    },
    openAdd() {
      this.adding = true;
      this.$nextTick(() => {
        this.$refs.amountInput.focus();
      });
    },
    // getWallets(driverId) {
    //   this.loading = true;
    //   this.$http
    //     .get(`/drivers/${this.driverId}/wallet-history`)
    //     .then(response => {
    //       this.wallets = response.data;
    //     })
    //     .catch(err => console.log(err))
    //     .finally(() => (this.loading = false));
    // },
    getAllWallets() {
      this.loading = true;
      var path = `/wallet-histories?populate=["driver","account","from"]&page=${this.currentPage}&limit=${this.perPage}`;
      if (this.driverId) {
        path += "&driver=" + this.driverId;
      }
      if (this.accountId) {
        path += "&account=" + this.accountId;
      }
      if (this.by && this.by != "all") {
        path += "&by=" + this.by;
      }
      if (this.startDate) {
        path += "&start=" + this.startDate;
      }
      if (this.endDate) {
        path += "&end=" + this.endDate;
      }
      this.$http
        .get(path)
        .then(response => {
          this.wallets = response.data.data;
          this.paginator = {
            nextPage: response.data.nextPage,
            prevPage: response.data.prevPage,
            count: response.data.count
          };
        })
        .catch(err => console.log(err))
        .finally(() => (this.loading = false));
    },
    searchDriver(e) {
      if (e.target.value.trim() === "") {
        this.driverSearchResults = [];
        this.driverId = null;
        this.getAllWallets();
      } else if (!isNaN(parseInt(e.target.value))) {
        this.driverSearchResults = this.drivers.filter(driver => {
          return driver.phoneNumber.slice(-4).includes(e.target.value);
        });
      } else {
        this.driverSearchResults = this.drivers.filter(driver => {
          let name = driver.firstName + " " + driver.lastName;
          return name.toLowerCase().includes(e.target.value.toLowerCase());
        });
      }
    },
    searchAccount(e) {
      if (e.target.value.trim() === "") {
        this.accountSearchResults = [];
        this.accountId = null;
        this.getAllWallets();
      } else if (!isNaN(parseInt(e.target.value))) {
        this.accountSearchResults = this.accounts.filter(account => {
          return account.phoneNumber.slice(-4).includes(e.target.value);
        });
      } else {
        this.accountSearchResults = this.accounts.filter(account => {
          let name = account.firstName + " " + account.lastName;
          return name.toLowerCase().includes(e.target.value.toLowerCase());
        });
      }
    },
    selectDriver(index) {
      let driver = this.driverSearchResults[index];
      this.selectedDriver = `${driver.firstName} ${driver.lastName} - ${driver.phoneNumber}`;
      this.driverId = driver._id;
      this.driverSearchResults = [];
      this.getAllWallets();
    },
    selectAccount(index) {
      let account = this.accountSearchResults[index];
      this.selectedAccount = `${account.firstName} ${account.lastName}`;
      this.accountId = account._id;
      this.accountSearchResults = [];
      this.by = "all";
      this.getAllWallets();
    },
    add() {
      this.busy = true;
      const amount = parseInt(this.amount);
      if (isNaN(amount) || amount <= 0) {
        this.amountError = true;
        this.busy = false;
        return false;
      }
      this.$http
        .post(`/drivers/${this.driverId}/top-up`, {
          amount,
          account: this.$store.state.auth.account._id,
          reason: this.reason ? this.reason : "Topup"
        })
        .then(() => {
          this.getAllWallets();
          window.flash({
            type: "success",
            body: "Wallet added successfully to driver!"
          });
          this.adding = false;
          this.amount = 0;
        })
        .catch(err => {
          console.log(err);
          window.flash({
            type: "error",
            body: "Failed to add wallet!"
          });
        })
        .finally(() => (this.busy = false));
    }
  }
};
</script>
